/**
 * Pure helpers for filtering and sorting places lists (no React).
 * Used by PlacesContext so logic is testable in isolation.
 */

/**
 * @param {Array} placesToSort
 * @param {string} sortOption
 * @returns {Array}
 */
export function applySorting(placesToSort, sortOption) {
	if (!placesToSort || placesToSort.length === 0) return placesToSort;

	const sortedPlaces = [...placesToSort];

	switch (sortOption) {
		case 'name':
			return sortedPlaces.sort((a, b) => {
				const nameA = a.displayName?.text || '';
				const nameB = b.displayName?.text || '';
				return nameA.localeCompare(nameB);
			});
		case 'rating':
			return sortedPlaces.sort((a, b) => (b.rating || 0) - (a.rating || 0));
		case 'reviews':
			return sortedPlaces.sort((a, b) => (b.userRatingCount || 0) - (a.userRatingCount || 0));
		case 'season':
			return sortedPlaces.sort((a, b) => {
				const seasonA = a.season || 0;
				const seasonB = b.season || 0;
				if (seasonA === seasonB) {
					return (a.episode || 0) - (b.episode || 0);
				}
				return seasonA - seasonB;
			});
		case 'newest':
			return sortedPlaces.sort((a, b) => {
				const dateA = new Date(a.createdAt || 0);
				const dateB = new Date(b.createdAt || 0);
				return dateB - dateA;
			});
		default:
			return sortedPlaces;
	}
}

/**
 * @param {Array} placesToFilter
 * @param {string} searchQuery
 * @param {object|null} mapBounds
 * @param {object} currentFilters
 * @returns {Array}
 */
export function applyFilters(placesToFilter, searchQuery, mapBounds, currentFilters) {
	let result = placesToFilter || [];

	if (searchQuery) {
		result = result.filter(
			(place) =>
				place.displayName?.text.toLowerCase().includes(searchQuery.toLowerCase()) ||
				place.shortFormattedAddress?.toLowerCase().includes(searchQuery.toLowerCase()) ||
				place.formattedAddress?.toLowerCase().includes(searchQuery.toLowerCase())
		);
	}

	if (mapBounds) {
		result = result.filter((place) => {
			let lat;
			let lng;

			if (place.location?.coordinates && Array.isArray(place.location.coordinates)) {
				lng = place.location.coordinates[0];
				lat = place.location.coordinates[1];
			} else if (place.location?.latitude && place.location?.longitude) {
				lat = place.location.latitude;
				lng = place.location.longitude;
			} else {
				return false;
			}

			return (
				lat >= mapBounds.south &&
				lat <= mapBounds.north &&
				lng >= mapBounds.west &&
				lng <= mapBounds.east
			);
		});
	}

	if (currentFilters?.propertyTypes && currentFilters.propertyTypes.length > 0) {
		result = result.filter((place) => {
			if (!place.types || !Array.isArray(place.types)) return false;
			return currentFilters.propertyTypes.some((type) => place.types.includes(type));
		});
	}

	if (currentFilters?.rating && currentFilters.rating > 0) {
		result = result.filter((place) => {
			const rating = place.rating || 0;
			return rating >= currentFilters.rating;
		});
	}

	if (currentFilters?.amenities && currentFilters.amenities.length > 0) {
		result = result.filter((place) => {
			const simulatedAmenities = [];
			const rating = place.rating || 3;
			const types = place.types || [];

			if (rating >= 4) simulatedAmenities.push('wifi', 'parking', 'ac');
			if (rating >= 4.5) simulatedAmenities.push('breakfast', 'pool');
			if (types.includes('bed_and_breakfast')) simulatedAmenities.push('breakfast');
			if (types.includes('hotel')) simulatedAmenities.push('wifi', 'tv', 'ac');

			return currentFilters.amenities.every((amenity) => simulatedAmenities.includes(amenity));
		});
	}

	if (currentFilters?.rooms) {
		const { bedrooms, beds, bathrooms } = currentFilters.rooms;
		if (bedrooms > 0 || beds > 0 || bathrooms > 0) {
			result = result.filter((place) => {
				const rating = place.rating || 3;
				const types = place.types || [];

				let simulatedBedrooms = 1;
				let simulatedBeds = 1;
				let simulatedBathrooms = 1;

				if (types.includes('hotel') && rating >= 4) {
					simulatedBedrooms = 2;
					simulatedBeds = 2;
					simulatedBathrooms = 2;
				}

				return (
					(bedrooms === 0 || simulatedBedrooms >= bedrooms) &&
					(beds === 0 || simulatedBeds >= beds) &&
					(bathrooms === 0 || simulatedBathrooms >= bathrooms)
				);
			});
		}
	}

	if (currentFilters?.bookingOptions && currentFilters.bookingOptions.length > 0) {
		result = result.filter((place) => {
			const rating = place.rating || 3;
			const simulatedOptions = [];

			if (rating >= 4) simulatedOptions.push('instant');
			if (rating >= 4.2) simulatedOptions.push('selfCheckin');
			if (rating >= 3.8) simulatedOptions.push('freeCancel');
			if (rating >= 3.5) simulatedOptions.push('pets');

			return currentFilters.bookingOptions.every((option) => simulatedOptions.includes(option));
		});
	}

	if (currentFilters?.seasons && currentFilters.seasons.length > 0) {
		result = result.filter((place) => {
			const season = place.season;
			return currentFilters.seasons.includes(season);
		});
	}

	return result;
}

/**
 * @param {object} params
 * @param {string} params.nameQuery
 * @param {object} params.filters
 * @returns {number}
 */
export function getActiveFiltersCount({ nameQuery, filters }) {
	let count = 0;

	if (nameQuery && nameQuery.trim()) count++;

	if (filters.amenities && filters.amenities.length > 0) count++;

	if (filters.rating && filters.rating > 0) count++;

	if (filters.propertyTypes && filters.propertyTypes.length > 0) count++;

	if (
		filters.rooms &&
		(filters.rooms.bedrooms > 0 || filters.rooms.beds > 0 || filters.rooms.bathrooms > 0)
	)
		count++;

	if (filters.bookingOptions && filters.bookingOptions.length > 0) count++;

	if (filters.seasons && filters.seasons.length > 0) count++;

	return count;
}
